import { i as initWangsUIStore, u as usePreset, g as getPreset } from './p-BU8h34na.js';

/**
 * BaseComponent is an abstract utility class that provides shared logic
 * for Stencil components. It currently includes support for handling UI
 * presets (such as class/style attributes) but is designed to be extended
 * with more common functionality in the future.
 *
 * @template Preset - A map of configurable sections with attributes or generators.
 */
class BaseComponent {
    preset;
    /**
     * Initializes the BaseComponent with either the provided preset or a globally registered one.
     *
     * @param tag - The tag name of the component used to look up the preset in the global store.
     * @param pt - Optional custom preset to override the global preset.
     */
    constructor({ tag, pt }) {
        initWangsUIStore();
        this.preset = usePreset(tag, pt);
        /**
         * Bind methods to preserve the correct `this` context.
         *
         * This is necessary because Stencil does not support extending base classes
         * in component classes directly (e.g., `class MyComponent extends BaseComponent`),
         * so we use composition instead.
         *
         * As a result, public utility methods from the base class (like `cx`, `sx`, `ptm`, `ptmo`)
         * are exposed by referencing them through getters or assigning them directly.
         * However, if these methods are passed around (e.g., used as callbacks or destructured),
         * their `this` context will be lost unless explicitly bound.
         *
         * Binding them here ensures they retain the correct reference to the internal state
         * and behave as expected when invoked outside the class.
         *
         * @example
         *  get cx() {
         *    return this.base.cx; // ⚠️ without binding, this.base.cx becomes an unbound internal method
         *  }
         *
         *  this.cx('header'); // ❌ will throw "this.getSection is not a function" if not bound,
         *                     // its because getSection is private method, and doesn't exist on outside class 'this' context
         *
         *  // ✅ Fix: Bind in constructor
         *  this.cx = this.cx.bind(this);
         */
        this.cx = this.cx.bind(this);
        this.sx = this.sx.bind(this);
        this.ptm = this.ptm.bind(this);
        this.ptmo = this.ptmo.bind(this);
    }
    /**
     * Retrieves a section of attributes from a given preset.
     *
     * @template P - The preset object type.
     * @template K - The section key within the preset.
     * @param preset - The preset object to query.
     * @param name - The key identifying the section to retrieve.
     * @param params - Optional parameters to pass if the section is a function.
     * @returns The resolved preset attributes or `undefined`.
     */
    getSection(preset, name, params) {
        const section = preset[name];
        return typeof section === 'function' ? section(params) : section;
    }
    /**
     * Retrieves a global preset by component tag name.
     *
     * @param tag - The tag name of the component.
     * @returns The global preset associated with the tag.
     */
    getPT(tag) {
        return getPreset(tag);
    }
    /**
     * Transforms a raw class definition into a normalized `class` string.
     *
     * Accepts several formats including:
     * - A single class name string.
     * - An array of class names.
     * - A conditional map of class names to booleans.
     * - An array of conditional maps.
     *
     * @param classes The raw class value to transform.
     * @returns A space-separated string of class names.
     */
    transformClasses(classes) {
        switch (true) {
            case typeof classes === 'string':
                return classes;
            case Array.isArray(classes):
                return classes
                    .map(item => {
                    if (typeof item === 'string')
                        return item;
                    if (typeof item === 'object' && item !== null) {
                        return Object.entries(item)
                            .filter(([_, value]) => Boolean(value))
                            .map(([key]) => key)
                            .join(' ');
                    }
                    return '';
                })
                    .filter(Boolean)
                    .join(' ');
            case typeof classes === 'object' && classes !== null:
                return Object.entries(classes)
                    .filter(([_, value]) => Boolean(value))
                    .map(([key]) => key)
                    .join(' ');
            default:
                return '';
        }
    }
    /**
     * Transforms a raw style definition into a normalized `style` string.
     *
     * Accepts:
     * - A string of inline CSS styles.
     * - An object with style properties in kebab-case or camelCase.
     *
     * @param style The raw style value to transform.
     * @returns A normalized inline CSS string.
     */
    transformStyles(style) {
        if (typeof style === 'string') {
            const result = {};
            style
                .split(';')
                .map(s => s.trim())
                .filter(Boolean)
                .forEach(decl => {
                const [prop, value] = decl.split(':').map(part => part.trim());
                if (prop && value) {
                    const camelCaseProp = prop.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
                    result[camelCaseProp] = value;
                }
            });
            return result;
        }
        if (typeof style === 'object' && style !== null) {
            const result = {};
            for (const key in style) {
                if (Object.prototype.hasOwnProperty.call(style, key)) {
                    const camelCaseKey = key.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
                    result[camelCaseKey] = style[key]?.toString();
                }
            }
            return result;
        }
        return {};
    }
    /**
     * Returns the `class` attribute from the current component's preset section.
     *
     * @param name - The section key to extract the class from.
     * @param params - Optional parameters if the section is a function.
     * @returns A `class` string or `undefined`.
     */
    cx(name, params) {
        return this.transformClasses(this.getSection(this.preset, name, params)?.class);
    }
    /**
     * Returns the `style` attribute from the current component's preset section.
     *
     * @param name - The section key to extract the style from.
     * @param params - Optional parameters if the section is a function.
     * @returns A `style` object or `undefined`.
     */
    sx(name, params) {
        return this.transformStyles(this.getSection(this.preset, name, params)?.style);
    }
    /**
     * Returns all preset attributes for a section in the current component.
     *
     * @param name - The section key to retrieve attributes from.
     * @param params - Optional parameters if the section is a function.
     * @returns A shallow copy of the preset attributes or `undefined`.
     */
    ptm(name, params) {
        const section = this.getSection(this.preset, name, params);
        return section ? { ...section, 'class': this.transformClasses(section.class), 'style': this.transformStyles(section.style), 'data-ui-section': name } : undefined;
    }
    /**
   * Returns all preset attributes for a section from another component's preset.
   *
   * @param tag - The tag name of the other component (e.g., 'wangs-tab-menu').
   * @param name - The section key to retrieve from that preset.
   * @param params - Optional parameters if the section is a function.
   * @returns A shallow copy of the preset attributes or `undefined`.
   */
    ptmo(tag, name, params) {
        const section = this.getSection(this.getPT(tag), name, params);
        return section
            ? {
                ...section,
                class: this.transformClasses(section.class),
                style: this.transformStyles(section.style),
                'data-ui-section': name,
            }
            : undefined;
    }
}

export { BaseComponent as B };
